// Ansicht Monitor / HMI.
//
// „Lauf starten" → POST /projects/{id}/runs, dann WS /ws/v1/projects/{id}/monitor abonnieren.
// Live-Trend (Liniendiagramm der Tag-Werte über die Zeit, eigene SVG-Chart-Komponente) plus eine
// Werteliste je Tag. „Lauf stoppen" → DELETE. Sauberes Verbinden/Trennen; bei unerwartetem
// Abbruch ein Reconnect-Hinweis. Der Kanal ist ein passiver Mitleser — read-only.

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import { useMonitorRun, type ConnState, type PaceState } from '../state/useMonitorRun';
import { getDataSources, getMe, getProject, hasEditRight, updateProjectSettings } from '../api/client';
import type { CsvEndBehavior, DataSource, RunCsvSpec, RunSourceSpec } from '../api/types';
import { anzeigeMitBeleg } from '../graph/anzeige';
import { TrendChart, type Series, type XDomain } from '../components/TrendChart';
import { XYChart } from '../components/XYChart';
import { Button, EmptyState, LabeledInput, LabeledSelect, PanelHeading } from '../components/ui';

// Farbpalette der Trendlinien (deterministisch je Tag-Index).
const LINE_COLORS = ['#C49B18', '#4FC3F7', '#81C784', '#FF8A65', '#BA68C8', '#F06292'];

// Auswählbare Zeitfenster (Sekunden; null = ganze Aufzeichnung).
const WINDOW_PRESETS: { key: string; s: number | null }[] = [
  { key: '30s', s: 30 },
  { key: '1min', s: 60 },
  { key: '5min', s: 300 },
  { key: '15min', s: 900 },
  { key: 'all', s: null },
];

export function MonitorView(): JSX.Element {
  const { t } = useI18n();
  const { activeProject, catalog, reportError } = useStore();
  // WS-/Lauf-Anbindung teilt sich diese Ansicht mit dem Dashboard (useMonitorRun).
  const { runId, owned, conn, busy, series, latest, pace, start, startLive, stop, reconnect, setPace } =
    useMonitorRun();

  // Projekt-Meta (opcEndpoint + Standard-Zykluszeit) und das eigene Recht „editieren". Die Meta
  // trägt den Live-Endpoint; ohne Recht ist das Einstellungen-Panel nur lesbar (Issue #121).
  const [opcEndpoint, setOpcEndpoint] = useState<string | null>(null);
  const [defaultCycleMs, setDefaultCycleMs] = useState<number | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  // Lauf-Modus: Simulation (bestehender dry-run-Weg) oder Live (OPC-Demonstrator).
  const [runMode, setRunMode] = useState<'sim' | 'live'>('sim');

  useEffect(() => {
    if (!activeProject) return;
    let cancelled = false;
    void getProject(activeProject.id)
      .then((meta) => {
        if (cancelled) return;
        setOpcEndpoint(meta.opcEndpoint ?? null);
        setDefaultCycleMs(meta.defaultCycleMs);
      })
      .catch((err) => {
        if (!cancelled) reportError(err, t('settings.project.loadFailed'));
      });
    void getMe()
      .then((me) => !cancelled && setCanEdit(hasEditRight(me.roles)))
      .catch(() => {
        /* ohne getMe bleibt das Panel read-only — sicherer Default */
      });
    return () => {
      cancelled = true;
    };
  }, [activeProject, reportError, t]);
  // Ein lesend angehängter Fremdlauf (owned === false) blockiert die Steuerung: kein Start (es
  // läuft schon einer), kein Stop (nicht unser Lauf). „Lauf starten" nur, wenn gar keiner läuft.
  const attached = runId !== null && !owned;

  // Fester Eingangs-Sollwert je catalog-input-Tag: der Sim-Treiber speist damit eine
  // konstante Quelle, sodass die Ausgangsreihe vorhersagbar ist (deterministischer Dry-Run).
  const [sourceValues, setSourceValues] = useState<Record<string, string>>({});

  // Quellenauswahl: Simulation (feste Sollwerte) oder CSV-Replay (aufgezeichnete Daten). Bei CSV
  // wird eine gespeicherte Datenquelle gewählt und das Ende-Verhalten (+Loop) je Lauf konfiguriert.
  const [sourceKind, setSourceKind] = useState<'sim' | 'csv'>('sim');
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [csvSourceId, setCsvSourceId] = useState<string>('');
  const [endBehavior, setEndBehavior] = useState<CsvEndBehavior>('stop');
  const [loopCount, setLoopCount] = useState<string>('0');
  const [loopGap, setLoopGap] = useState<string>('0');

  // Trend-Ansicht: gewähltes Zeitfenster (Sekunden; null = alles), „Neuestem folgen" und ein
  // optionales, per Zoom/Pan gesetztes Fenster (überschreibt Fenster+Folgen, bis „Zurücksetzen").
  const [windowS, setWindowS] = useState<number | null>(60);
  const [follow, setFollow] = useState(true);
  const [viewDomain, setViewDomain] = useState<XDomain | null>(null);
  // Diagramm-Modus: Trend (Wert über Zeit) oder XY-Phasendiagramm (zwei Signale gegeneinander).
  const [chartMode, setChartMode] = useState<'trend' | 'xy'>('trend');
  const [xyX, setXyX] = useState<string>('');
  const [xyY, setXyY] = useState<string>('');

  // Gespeicherte Datenquellen des Projekts laden (für die CSV-Option), best-effort.
  useEffect(() => {
    if (!activeProject) return;
    let cancelled = false;
    void getDataSources(activeProject.id)
      .then((ds) => {
        if (cancelled) return;
        setDataSources(ds.sources);
        if (ds.sources.length > 0) setCsvSourceId((prev) => prev || ds.sources[0].id);
      })
      .catch(() => {
        /* keine Datenquellen — dann bleibt nur die Simulation */
      });
    return () => {
      cancelled = true;
    };
  }, [activeProject]);

  // Katalog-Eingänge (direction === 'input') sind die speisbaren Tags des Sim-Laufs.
  const inputTags = (catalog?.variables ?? []).filter((v) => v.direction === 'input');

  const startRun = useCallback(() => {
    // Live (OPC-Demonstrator): den gekapselten Live-Weg gehen (propose → approve → run mode=live
    // gegen den Projekt-OPC-Endpoint). Fehlt der Endpoint, zeigt startLive den Hinweis und startet
    // nicht — der Sim-Weg bleibt davon unberührt.
    if (runMode === 'live') {
      void startLive(opcEndpoint);
      return;
    }
    if (sourceKind === 'csv') {
      // CSV-Replay: die gewählte gespeicherte Quelle mit dem hier gewählten Ende-Verhalten fahren.
      const csv: RunCsvSpec = {
        sourceId: csvSourceId,
        endBehavior,
        loopCount: endBehavior === 'loop' ? Number(loopCount) || 0 : undefined,
        loopGapS: endBehavior === 'loop' ? Number(loopGap) || 0 : undefined,
      };
      void start({ csv });
      return;
    }
    // Simulation: für jeden gesetzten Eingangs-Sollwert eine konstante Quelle mitgeben; leere
    // Felder bleiben beim Loopback-Standardwert des Sim-Treibers.
    const sources: RunSourceSpec[] = inputTags
      .map((v) => ({ tag: v.tag, raw: sourceValues[v.tag] }))
      .filter((s) => s.raw !== undefined && s.raw.trim() !== '' && Number.isFinite(Number(s.raw)))
      .map((s) => ({ tag: s.tag, kind: 'const' as const, level: Number(s.raw) }));
    void start(sources);
  }, [
    runMode,
    opcEndpoint,
    startLive,
    sourceKind,
    csvSourceId,
    endBehavior,
    loopCount,
    loopGap,
    inputTags,
    sourceValues,
    start,
  ]);

  if (!activeProject) {
    return (
      <div className="p-8">
        <EmptyState title={t('common.noProject')} hint={t('common.noProjectHint')} />
      </div>
    );
  }

  const tags = [...series.keys()];
  const chartSeries: Series[] = tags.map((tag, i) => ({
    tag,
    color: LINE_COLORS[i % LINE_COLORS.length],
    points: series.get(tag) ?? [],
  }));

  // Zeitspanne aller Datenpunkte (für Fenster/Follow und die Zoom-Knöpfe).
  const dataBounds = useMemo((): { tMin: number; tMax: number } | null => {
    let tMin = Infinity;
    let tMax = -Infinity;
    for (const s of chartSeries) {
      for (const p of s.points) {
        if (p.t < tMin) tMin = p.t;
        if (p.t > tMax) tMax = p.t;
      }
    }
    return Number.isFinite(tMin) ? { tMin, tMax } : null;
  }, [chartSeries]);

  // Sichtbares Zeitfenster: ein per Zoom/Pan gesetztes Fenster hat Vorrang; sonst folgt es (bei
  // aktivem „Neuestem folgen") dem gewählten Fenster am rechten Rand; sonst die ganze Aufzeichnung.
  const xDomain: XDomain | undefined = useMemo(() => {
    if (viewDomain) return viewDomain;
    if (!dataBounds) return undefined;
    if (windowS != null && follow) {
      return [Math.max(dataBounds.tMin, dataBounds.tMax - windowS), dataBounds.tMax];
    }
    return undefined;
  }, [viewDomain, dataBounds, windowS, follow]);

  const effectiveDomain: XDomain | null = xDomain ?? (dataBounds ? [dataBounds.tMin, dataBounds.tMax] : null);

  // Zoom um die Fenstermitte (Faktor <1 = hinein, >1 = heraus). Setzt ein explizites Fenster und
  // pausiert das Folgen, damit der Ausschnitt stehen bleibt.
  const zoomBy = useCallback(
    (factor: number) => {
      if (!effectiveDomain) return;
      const [a, b] = effectiveDomain;
      const center = (a + b) / 2;
      const half = ((b - a) * factor) / 2;
      setViewDomain([center - half, center + half]);
      setFollow(false);
    },
    [effectiveDomain],
  );

  const resetView = useCallback(() => {
    setViewDomain(null);
    setFollow(true);
  }, []);

  const onWindowChange = useCallback((key: string) => {
    const preset = WINDOW_PRESETS.find((w) => w.key === key);
    setWindowS(preset ? preset.s : null);
    setViewDomain(null);
    setFollow(true);
  }, []);

  const currentWindowKey =
    WINDOW_PRESETS.find((w) => w.s === windowS)?.key ?? (windowS == null ? 'all' : '1min');

  return (
    <div className="flex h-full flex-col" data-testid="monitor-view">
      {/* Projekt-Einstellungen: OPC-Endpoint + Standard-Zykluszeit (Issue #121). Aufklappbar, kompakt. */}
      <ProjectSettingsPanel
        projectId={activeProject.id}
        opcEndpoint={opcEndpoint}
        defaultCycleMs={defaultCycleMs}
        canEdit={canEdit}
        onSaved={(meta) => {
          setOpcEndpoint(meta.opcEndpoint ?? null);
          setDefaultCycleMs(meta.defaultCycleMs);
        }}
      />

      {/* Steuerleiste */}
      <div className="flex flex-wrap items-center gap-3 border-b border-bf-border px-4 py-3">
        {/* Lauf-Modus: Simulation (dry-run) oder Live (OPC-Demonstrator). */}
        {!runId && (
          <div className="w-52" data-testid="monitor-mode">
            <LabeledSelect
              label={t('monitor.mode.label')}
              value={runMode}
              onChange={(v) => setRunMode(v as 'sim' | 'live')}
            >
              <option value="sim">{t('monitor.modeSim')}</option>
              <option value="live">{t('monitor.modeLive')}</option>
            </LabeledSelect>
          </div>
        )}

        {/* Live: der Endpoint kommt aus den Projekt-Einstellungen; fehlt er, ein klarer Hinweis. */}
        {!runId && runMode === 'live' && (
          <div className="flex items-center text-xs" data-testid="monitor-live-endpoint">
            {opcEndpoint && opcEndpoint.trim() !== '' ? (
              <span className="text-bf-muted">
                {t('monitor.live.endpoint')}:{' '}
                <span className="font-mono text-bf-gold">{opcEndpoint}</span>
              </span>
            ) : (
              <span className="text-amber-300" data-testid="monitor-live-no-endpoint">
                {t('monitor.live.noEndpoint')}
              </span>
            )}
          </div>
        )}

        {/* Quellenauswahl: Simulation oder CSV-Replay (nur im Sim-Modus). */}
        {!runId && runMode === 'sim' && (
          <div className="w-40" data-testid="monitor-source-kind">
            <LabeledSelect
              label={t('monitor.csv.source')}
              value={sourceKind}
              onChange={(v) => setSourceKind(v as 'sim' | 'csv')}
            >
              <option value="sim">{t('monitor.csv.sim')}</option>
              <option value="csv">{t('monitor.csv.replay')}</option>
            </LabeledSelect>
          </div>
        )}

        {/* Simulation: feste Eingangs-Sollwerte je catalog-input-Tag (deterministischer Sim-Lauf). */}
        {!runId &&
          runMode === 'sim' &&
          sourceKind === 'sim' &&
          inputTags.map((v) => (
            <div key={v.tag} className="w-32" data-testid={`monitor-source-${v.tag}`}>
              <LabeledInput
                label={anzeigeMitBeleg(v)}
                type="number"
                value={sourceValues[v.tag] ?? ''}
                placeholder="0"
                onChange={(val) => setSourceValues((prev) => ({ ...prev, [v.tag]: val }))}
              />
            </div>
          ))}

        {/* CSV-Replay: Datenquelle wählen, Ende-Verhalten (+Loop-Konfig). */}
        {!runId && runMode === 'sim' && sourceKind === 'csv' && (
          <div className="flex flex-wrap items-end gap-3" data-testid="monitor-csv-options">
            <div className="w-48">
              <LabeledSelect
                label={t('monitor.csv.replay')}
                value={csvSourceId}
                onChange={setCsvSourceId}
              >
                {dataSources.length === 0 && <option value="">{t('monitor.csv.chooseSource')}</option>}
                {dataSources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label ?? s.file}
                  </option>
                ))}
              </LabeledSelect>
            </div>
            <div className="w-44">
              <LabeledSelect
                label={t('monitor.csv.endBehavior')}
                value={endBehavior}
                onChange={(v) => setEndBehavior(v as CsvEndBehavior)}
              >
                <option value="stop">{t('monitor.csv.end.stop')}</option>
                <option value="loop">{t('monitor.csv.end.loop')}</option>
                <option value="hold">{t('monitor.csv.end.hold')}</option>
              </LabeledSelect>
            </div>
            {endBehavior === 'loop' && (
              <>
                <div className="w-40" data-testid="monitor-csv-loopcount">
                  <LabeledInput
                    label={t('monitor.csv.loopCount')}
                    type="number"
                    value={loopCount}
                    onChange={setLoopCount}
                  />
                </div>
                <div className="w-40">
                  <LabeledInput
                    label={t('monitor.csv.loopGap')}
                    type="number"
                    value={loopGap}
                    onChange={setLoopGap}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {attached ? (
          <span data-testid="monitor-attached" className="text-xs text-bf-muted">
            {t('monitor.attached')}
          </span>
        ) : !runId ? (
          <span data-testid="monitor-start">
            <Button
              variant="primary"
              onClick={startRun}
              disabled={
                busy || (runMode === 'sim' && sourceKind === 'csv' && !csvSourceId)
              }
            >
              {runMode === 'live' ? t('monitor.startLive') : t('monitor.start')}
            </Button>
          </span>
        ) : (
          <span data-testid="monitor-stop">
            <Button variant="danger" onClick={() => void stop()} disabled={busy}>
              {t('monitor.stop')}
            </Button>
          </span>
        )}
        <span data-testid="monitor-conn" data-conn={conn}>
          <ConnBadge state={conn} />
        </span>
        {conn === 'lost' && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-amber-300">{t('monitor.lost')}</span>
            <Button onClick={reconnect}>{t('monitor.reconnect')}</Button>
          </div>
        )}
        {runId && <span className="text-xs text-bf-muted">{runId}</span>}
      </div>

      {/* Geschwindigkeit: Zykluszeit (Regel-dt) und Zeitraffer, live verstellbar. Nur bei aktivem Lauf. */}
      {runId && pace && <SpeedPanel pace={pace} onChange={setPace} />}

      <div className="grid flex-1 grid-cols-[1fr_18rem] overflow-hidden">
        {/* Trend */}
        <div className="flex flex-col p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <PanelHeading>{chartMode === 'xy' ? t('monitor.xy') : t('monitor.trend')}</PanelHeading>
              {tags.length > 0 && (
                <div className="flex overflow-hidden rounded border border-bf-border text-xs">
                  {(['trend', 'xy'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-testid={`monitor-chartmode-${m}`}
                      onClick={() => setChartMode(m)}
                      className={`px-2 py-1 ${chartMode === m ? 'bg-bf-gold text-bf-navy' : 'text-bf-white hover:bg-bf-navy-light'}`}
                    >
                      {t(`monitor.chart.${m}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {tags.length > 0 && chartMode === 'xy' && (
              <div className="flex items-center gap-2" data-testid="monitor-xy-controls">
                <label className="flex items-center gap-1 text-xs text-bf-muted">
                  X
                  <select
                    data-testid="monitor-xy-x"
                    className="rounded border border-bf-border bg-bf-navy-light px-1.5 py-1 text-xs text-bf-white"
                    value={xyX || tags[0]}
                    onChange={(e) => setXyX(e.target.value)}
                  >
                    {tags.map((tg) => (
                      <option key={tg} value={tg}>
                        {tg}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-1 text-xs text-bf-muted">
                  Y
                  <select
                    data-testid="monitor-xy-y"
                    className="rounded border border-bf-border bg-bf-navy-light px-1.5 py-1 text-xs text-bf-white"
                    value={xyY || tags[Math.min(1, tags.length - 1)]}
                    onChange={(e) => setXyY(e.target.value)}
                  >
                    {tags.map((tg) => (
                      <option key={tg} value={tg}>
                        {tg}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            {tags.length > 0 && chartMode === 'trend' && (
              <div className="flex items-center gap-2" data-testid="monitor-view-controls">
                <label className="flex items-center gap-1 text-xs text-bf-muted">
                  {t('monitor.view.window')}
                  <select
                    data-testid="monitor-window"
                    className="rounded border border-bf-border bg-bf-navy-light px-1.5 py-1 text-xs text-bf-white"
                    value={currentWindowKey}
                    onChange={(e) => onWindowChange(e.target.value)}
                  >
                    {WINDOW_PRESETS.map((w) => (
                      <option key={w.key} value={w.key}>
                        {w.key === 'all' ? t('monitor.view.all') : w.key}
                      </option>
                    ))}
                  </select>
                </label>
                <Button testId="monitor-zoom-in" onClick={() => zoomBy(1 / 1.5)}>
                  {t('monitor.view.zoomIn')}
                </Button>
                <Button testId="monitor-zoom-out" onClick={() => zoomBy(1.5)}>
                  {t('monitor.view.zoomOut')}
                </Button>
                <label className="flex items-center gap-1 text-xs text-bf-muted">
                  <input
                    data-testid="monitor-follow"
                    type="checkbox"
                    checked={follow && !viewDomain}
                    onChange={(e) => {
                      setFollow(e.target.checked);
                      if (e.target.checked) setViewDomain(null);
                    }}
                  />
                  {t('monitor.view.follow')}
                </label>
                <Button testId="monitor-view-reset" onClick={resetView}>
                  {t('monitor.view.reset')}
                </Button>
              </div>
            )}
          </div>
          <div className="flex-1">
            {tags.length === 0 ? (
              <EmptyState title={t('monitor.noData')} hint={t('monitor.noDataHint')} />
            ) : chartMode === 'xy' ? (
              <XYChart
                series={{
                  xTag: xyX || tags[0],
                  yTag: xyY || tags[Math.min(1, tags.length - 1)],
                  color: LINE_COLORS[0],
                  x: series.get(xyX || tags[0]) ?? [],
                  y: series.get(xyY || tags[Math.min(1, tags.length - 1)]) ?? [],
                }}
                emptyLabel={t('monitor.noData')}
              />
            ) : (
              <TrendChart
                series={chartSeries}
                emptyLabel={t('monitor.noData')}
                xDomain={xDomain}
                onXDomainChange={(d) => {
                  setViewDomain(d);
                  setFollow(false);
                }}
              />
            )}
          </div>
        </div>

        {/* Werteliste je Tag */}
        <aside className="overflow-y-auto border-l border-bf-border p-4">
          <PanelHeading>{t('monitor.values')}</PanelHeading>
          {tags.length === 0 && <p className="text-xs text-bf-muted">{t('monitor.noValues')}</p>}
          <div className="flex flex-col gap-1.5">
            {tags.map((tag, i) => {
              const v = latest.get(tag);
              return (
                <div
                  key={tag}
                  data-testid={`monitor-value-${tag}`}
                  data-value={v ? String(v.value) : ''}
                  className="flex items-center justify-between rounded border border-bf-border px-2 py-1.5"
                >
                  <span className="flex items-center gap-2 text-sm text-bf-white">
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ background: LINE_COLORS[i % LINE_COLORS.length] }}
                    />
                    {tag}
                  </span>
                  <span className="text-sm tabular-nums text-bf-gold">
                    {v ? formatValue(v.value) : '—'}
                    {v?.unit ? <span className="ml-1 text-xs text-bf-muted">{v.unit}</span> : null}
                  </span>
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
}

function ConnBadge({ state }: { state: ConnState }): JSX.Element {
  const { t } = useI18n();
  const map: Record<ConnState, { label: string; color: string }> = {
    idle: { label: t('monitor.conn.idle'), color: 'bg-bf-muted' },
    connecting: { label: t('monitor.conn.connecting'), color: 'bg-amber-400' },
    live: { label: t('monitor.conn.live'), color: 'bg-emerald-400' },
    lost: { label: t('monitor.conn.lost'), color: 'bg-red-400' },
  };
  const s = map[state];
  return (
    <span className="flex items-center gap-2 text-xs text-bf-muted">
      <span className={`inline-block h-2 w-2 rounded-full ${s.color}`} />
      {s.label}
    </span>
  );
}

// Geschwindigkeits-Regler: Zykluszeit (Regel-dt) und Zeitraffer-Faktor, live per Schieberegler.
// Der Zeitraffer ist bei Live-OPC (pace.lockSpeed) gesperrt und wird als „Echtzeit" ausgewiesen.
function SpeedPanel({
  pace,
  onChange,
}: {
  pace: PaceState;
  onChange: (change: { cycleMs?: number; speed?: number }) => void;
}): JSX.Element {
  const { t } = useI18n();
  const cycleS = pace.cycleMs / 1000;
  return (
    <div
      className="flex flex-wrap items-center gap-6 border-b border-bf-border px-4 py-2.5"
      data-testid="monitor-speed"
    >
      <span className="text-xs font-medium uppercase tracking-wide text-bf-muted">
        {t('monitor.speed.heading')}
      </span>

      {/* Zykluszeit (Regel-dt) 0,1–10 s */}
      <label className="flex items-center gap-2 text-xs text-bf-white">
        <span className="text-bf-muted">{t('monitor.speed.cycle')}</span>
        <input
          data-testid="monitor-cycle-slider"
          type="range"
          min={0.1}
          max={10}
          step={0.1}
          value={cycleS}
          className="w-40 accent-bf-gold"
          onChange={(e) => onChange({ cycleMs: Math.round(Number(e.target.value) * 1000) })}
        />
        <span className="w-14 tabular-nums text-bf-gold">{cycleS.toFixed(1)} s</span>
      </label>

      {/* Zeitraffer-Faktor 0,25–20× (bei Live-OPC gesperrt) */}
      <label className="flex items-center gap-2 text-xs text-bf-white">
        <span className="text-bf-muted">{t('monitor.speed.factor')}</span>
        <input
          data-testid="monitor-speed-slider"
          type="range"
          min={0.25}
          max={20}
          step={0.25}
          value={pace.speed}
          disabled={pace.lockSpeed}
          className="w-40 accent-bf-gold disabled:opacity-40"
          onChange={(e) => onChange({ speed: Number(e.target.value) })}
        />
        <span className="w-14 tabular-nums text-bf-gold">
          {pace.lockSpeed ? '1×' : `${pace.speed}×`}
        </span>
      </label>

      {pace.lockSpeed && (
        <span data-testid="monitor-speed-locked" className="text-xs text-bf-muted">
          {t('monitor.speed.locked')}
        </span>
      )}
    </div>
  );
}

// Projekt-Einstellungen-Panel (Issue #121): editierbarer OPC-Endpoint + Standard-Zykluszeit,
// aufklappbar am Kopf der Monitor-Ansicht. Zeigt die Werte aus getProject; Speichern ruft
// updateProjectSettings (PUT …/settings). Ohne Recht „editieren" ist das Panel read-only — die
// harte Durchsetzung bleibt beim Backend (403/422), diese Sperre spiegelt das nur.
function ProjectSettingsPanel({
  projectId,
  opcEndpoint,
  defaultCycleMs,
  canEdit,
  onSaved,
}: {
  projectId: string;
  opcEndpoint: string | null;
  defaultCycleMs: number | null;
  canEdit: boolean;
  onSaved: (meta: import('../api/types').ProjectMeta) => void;
}): JSX.Element {
  const { t } = useI18n();
  const { notify, reportError } = useStore();
  const [open, setOpen] = useState(false);
  const [endpoint, setEndpoint] = useState('');
  const [cycleMs, setCycleMs] = useState('');
  const [busy, setBusy] = useState(false);

  // Beim Aufklappen (und bei Wertänderung von außen) die Editierfelder mit dem aktuellen Stand füllen.
  useEffect(() => {
    setEndpoint(opcEndpoint ?? '');
    setCycleMs(defaultCycleMs != null ? String(defaultCycleMs) : '');
  }, [opcEndpoint, defaultCycleMs]);

  const save = useCallback(async () => {
    // Nur die tatsächlich geänderten Felder mitgeben (partielles Update). Ein leer gemachter
    // Endpoint wird bewusst als null gesendet (entfernt ihn); eine leere Zykluszeit bleibt ungesetzt.
    const trimmed = endpoint.trim();
    const nextEndpoint: string | null = trimmed === '' ? null : trimmed;
    const parsedCycle = Number(cycleMs);
    const settings: import('../api/types').ProjectSettingsUpdate = {};
    if (nextEndpoint !== (opcEndpoint ?? null)) settings.opcEndpoint = nextEndpoint;
    if (cycleMs.trim() !== '' && Number.isFinite(parsedCycle) && parsedCycle !== defaultCycleMs) {
      settings.defaultCycleMs = parsedCycle;
    }
    if (Object.keys(settings).length === 0) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const meta = await updateProjectSettings(projectId, settings);
      onSaved(meta);
      notify('success', t('settings.project.saved'));
      setOpen(false);
    } catch (err) {
      reportError(err, t('settings.project.saveFailed'));
    } finally {
      setBusy(false);
    }
  }, [endpoint, cycleMs, opcEndpoint, defaultCycleMs, projectId, onSaved, notify, reportError, t]);

  return (
    <div className="border-b border-bf-border" data-testid="monitor-settings-panel">
      <button
        type="button"
        data-testid="monitor-settings-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2 text-left text-xs text-bf-muted hover:text-bf-white"
      >
        <span className="flex items-center gap-2">
          <span className="font-semibold uppercase tracking-wider">
            {t('settings.project.heading')}
          </span>
          <span className="text-bf-muted">
            {t('settings.opcEndpoint')}:{' '}
            <span className="font-mono text-bf-white" data-testid="monitor-settings-endpoint-value">
              {opcEndpoint && opcEndpoint.trim() !== '' ? opcEndpoint : t('settings.project.notSet')}
            </span>
            {' · '}
            {t('settings.cycleTime')}:{' '}
            <span className="text-bf-white" data-testid="monitor-settings-cycle-value">
              {defaultCycleMs != null ? `${defaultCycleMs} ms` : '—'}
            </span>
          </span>
        </span>
        <span aria-hidden>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="flex flex-wrap items-end gap-3 px-4 pb-3" data-testid="monitor-settings-form">
          <div className="w-72">
            <LabeledInput
              label={t('settings.opcEndpoint')}
              value={endpoint}
              placeholder="opc.tcp://plc:4840"
              onChange={setEndpoint}
            />
          </div>
          <div className="w-40">
            <LabeledInput
              label={t('settings.cycleTime')}
              type="number"
              value={cycleMs}
              placeholder="1000"
              onChange={setCycleMs}
            />
          </div>
          {canEdit ? (
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={busy}
              testId="monitor-settings-save"
            >
              {t('settings.project.save')}
            </Button>
          ) : (
            <span className="text-xs text-bf-muted" data-testid="monitor-settings-readonly">
              {t('settings.project.readOnly')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function formatValue(v: number): string {
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  if (Math.abs(v) >= 1) return v.toFixed(2);
  return v.toFixed(3);
}
